import { Money } from '@klear/money'
import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import toNumber from 'lodash/toNumber'
import toString from 'lodash/toString'

import { Button } from '@klear/ui/components/button'
import { useTrader } from '@klear/ui/hooks/use-trader'
import { MoneyInput } from '@klear/ui/components/money-input'
import { useKlearFinancialAccount } from '@klear/ui/hooks/use-klear-financial-account'
import { ROUTE_EXTERNAL_BANK, ROUTE_KLEAR_ACCOUNT, ROUTE_SUPPLIERS } from '@klear/constants'
import { DEFAULT_CURRENCY_CODE } from '@klear/constants/currency'
import {
  PAYMENT_NETWORK_ACH,
  PAYMENT_NETWORK_WIRE,
  EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_UNVERIFIED,
  EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_PENDING,
  type BankPaymentNetwork
} from '@klear/bank-account/constants'
import { useTraderExternalBankAccounts } from '@klear/ui/hooks/use-trader-external-bank-accounts'
import { useSupplierExternalBankAccounts } from '@klear/ui/hooks/use-supplier-external-bank-accounts'
import { Label } from '@klear/ui/components/label'
import { Combobox } from '@klear/ui/components/combobox'
import { useSuppliers } from '@klear/ui/hooks/use-suppliers'
import { useBankPaymentMutations } from '@klear/ui/hooks/use-bank-payment'
import { PaymentNetworkOptions } from '@klear/payment/constants'
import { PaymentReferenceInput, validatePaymentReferenceInput } from '@klear/ui/components/payment-reference-input'
import { Alert, AlertDescription, AlertTitle } from '@klear/ui/components/alert'
import { Checkbox } from '@klear/ui/components/checkbox'
import { KlearAccountSelect } from '@klear/ui/components/klear-account-select'
import { KLEAR_ACCOUNT_TYPE_RECEIVABLE } from '@klear/klear-financial-account/constants'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

export default function MakePayment() {
  useDocumentTitle('Make a Payment')
  const { traderId } = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const urlSupplierId = searchParams.get('supplierId')
  const urlNetwork = searchParams.get('network')
  const urlUseUnverified = searchParams.get('useUnverified') === 'true'

  const { urlPrefixApp } = useTrader(traderId)
  const { suppliers } = useSuppliers(parseInt(traderId || '0'))
  const { openAccounts, klearFinancialAccount, receivableAccount } = useKlearFinancialAccount()
  const [paymentKlearAccountId, setPaymentKlearAccountId] = useState<number | null>(null)
  const paymentKlearAccount =
    openAccounts.find((a) => a.id === paymentKlearAccountId) ??
    (klearFinancialAccount?.isOpen ? klearFinancialAccount : null) ??
    openAccounts.find((a) => a.accountType === KLEAR_ACCOUNT_TYPE_RECEIVABLE) ??
    openAccounts[0] ??
    null

  useEffect(() => {
    if (paymentKlearAccountId != null && openAccounts.some((a) => a.id === paymentKlearAccountId)) return
    const defaultFromDashboard = klearFinancialAccount?.isOpen ? klearFinancialAccount.id : null
    const defaultFromReceivable = receivableAccount?.isOpen ? receivableAccount.id : null
    const defaultId = defaultFromDashboard ?? defaultFromReceivable ?? openAccounts[0]?.id ?? null
    if (defaultId != null) setPaymentKlearAccountId(defaultId)
  }, [
    paymentKlearAccountId,
    klearFinancialAccount?.id,
    klearFinancialAccount?.isOpen,
    receivableAccount?.id,
    receivableAccount?.isOpen,
    openAccounts
  ])

  const { createBankPayment, isCreating } = useBankPaymentMutations()
  const [selectedSupplierId, setSelectedSupplierId] = useState(urlSupplierId || '')
  const [selectedNetwork, setSelectedNetwork] = useState<BankPaymentNetwork | ''>(
    (urlNetwork as BankPaymentNetwork) || ''
  )
  const [selectedBankAccountId, setSelectedBankAccountId] = useState('')
  const [useUnverifiedAccount, setUseUnverifiedAccount] = useState(urlUseUnverified || false)

  const selectedSupplier = suppliers.find((supplier) => toString(supplier.id) === selectedSupplierId)
  const { supplierExternalBankAccounts: supplierBankAccounts, isLoading: isLoadingSupplierBankAccounts } =
    useSupplierExternalBankAccounts(selectedSupplier?.id)

  const { traderExternalBankAccounts: traderBankAccounts, isLoading: isLoadingTraderBankAccounts } =
    useTraderExternalBankAccounts()

  const unverifiedTraderAccounts = traderBankAccounts.filter(
    (account) =>
      (account.verificationStatus === EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_UNVERIFIED &&
        account.stripeSetupIntentId == null) ||
      account.verificationStatus === EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_PENDING
  )

  const handleAddSupplierBankAccount = () => {
    if (selectedSupplierId && selectedNetwork) {
      const params = new URLSearchParams({
        returnToPayment: 'true',
        supplierId: selectedSupplierId,
        network: selectedNetwork
      })

      navigate(
        `${urlPrefixApp}/${ROUTE_SUPPLIERS}/${selectedSupplierId}/${ROUTE_EXTERNAL_BANK}/new?${params.toString()}`
      )
    }
  }

  const [amount, setAmount] = useState(Money.zero(DEFAULT_CURRENCY_CODE))
  const [statementDescriptor, setStatementDescriptor] = useState('KlearPay')

  useEffect(() => {
    if (urlSupplierId || urlNetwork || urlUseUnverified) {
      const newSearchParams = new URLSearchParams(searchParams)
      if (urlSupplierId) {
        newSearchParams.delete('supplierId')
      }
      if (urlNetwork) {
        newSearchParams.delete('network')
      }
      if (urlUseUnverified) {
        newSearchParams.delete('useUnverified')
      }
      setSearchParams(newSearchParams)
    }
  }, [urlSupplierId, urlNetwork, urlUseUnverified, setSearchParams, searchParams])

  const selectedBankAccount = (useUnverifiedAccount ? unverifiedTraderAccounts : supplierBankAccounts).find(
    (account) => account.id != null && toString(account.id) === selectedBankAccountId
  )
  const addressMissing =
    selectedNetwork === PAYMENT_NETWORK_WIRE && selectedBankAccount != null && !selectedBankAccount.hasWireAddress
  const addressError = addressMissing ? { message: 'Recipient address is required for wire transfers' } : undefined

  const availableBalance = paymentKlearAccount?.account ? paymentKlearAccount.balanceAvailable : undefined
  const balanceCapsThisPayment = availableBalance != null && amount.currency === availableBalance.currency
  const balanceLabel = `Klear account available balance: ${(availableBalance ?? Money.zero()).toString()}`
  const balanceHelperText =
    availableBalance == null || balanceCapsThisPayment
      ? balanceLabel
      : `${balanceLabel} — this payment is in ${amount.currency}, so the balance does not cap the amount`

  const errors: string[] = []
  if (selectedBankAccountId && selectedNetwork) {
    if (addressMissing) errors.push('Recipient address is required for wire transfers')
    const referenceResult = validatePaymentReferenceInput(statementDescriptor, selectedNetwork)
    if (!referenceResult.ok) errors.push(referenceResult.message)
  }

  const supportedPaymentNetworkBankAccounts = (supplierBankAccounts || []).filter((account) =>
    account.supportedPaymentNetworks.includes(selectedNetwork as BankPaymentNetwork)
  )

  const handleClose = () => {
    setAmount(Money.zero(DEFAULT_CURRENCY_CODE))
    setStatementDescriptor('KlearPay')
    setSelectedSupplierId('')
    setSelectedNetwork('')
    setSelectedBankAccountId('')
    setUseUnverifiedAccount(false)
    const referrer = document.referrer
    const cameFromSameOrigin = referrer ? new URL(referrer).origin === window.location.origin : false
    if (cameFromSameOrigin) {
      navigate(-1)
    } else {
      navigate(`${urlPrefixApp}/${ROUTE_KLEAR_ACCOUNT}`)
    }
  }

  async function handleFormSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!selectedBankAccountId || !selectedNetwork || errors.length > 0) return
    if (openAccounts.length > 1 && paymentKlearAccount?.id == null) return
    try {
      await createBankPayment({
        amount: amount.toJSON(),
        statement_descriptor: statementDescriptor,
        network: selectedNetwork,
        external_bank_account_id: toNumber(selectedBankAccountId),
        supplier_id: useUnverifiedAccount ? undefined : toNumber(selectedSupplierId),
        klear_account_id: paymentKlearAccount?.id ?? undefined
      })
      handleClose()
    } catch (error) {
      console.error('Payment error:', error)
    }
  }
  return (
    <section className="h-full flex-1 flex-col space-y-8 p-8 md:flex">
      <div className="flex items-center justify-between space-y-2">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Make a Payment</h2>
        </div>
      </div>
      <form onSubmit={handleFormSubmit} className="flex flex-col gap-8">
        <KlearAccountSelect
          accounts={openAccounts}
          value={paymentKlearAccount?.id}
          onChange={setPaymentKlearAccountId}
          label="Pay from Klear Account"
        />

        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="use-unverified-account"
              checked={useUnverifiedAccount}
              onCheckedChange={(checked) => {
                setUseUnverifiedAccount(checked === true)
                setSelectedSupplierId('')
                setSelectedBankAccountId('')
                setSelectedNetwork('')
              }}
            />
            <Label htmlFor="use-unverified-account" className="text-sm font-normal cursor-pointer">
              I need to temporarily transfer funds to an unverified external bank account
            </Label>
          </div>
        </div>

        {!useUnverifiedAccount && (
          <div className="space-y-4">
            <div className="font-semibold text-lg">Step 1: Select Supplier</div>
            <div className="space-y-2">
              <Label>Supplier</Label>
              <Combobox
                placeholder="Select a supplier"
                options={suppliers.map((supplier) => ({
                  label: supplier.name,
                  value: toString(supplier.id)
                }))}
                value={selectedSupplierId}
                onChange={(value) => {
                  setSelectedSupplierId(value)
                  setSelectedBankAccountId('')
                }}
              />
            </div>
          </div>
        )}

        {((useUnverifiedAccount && unverifiedTraderAccounts.length > 0) || selectedSupplier) && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="font-semibold text-lg">Step 2: Select Payment Network</div>
            </div>
            <div className="space-y-2">
              <Label>Payment Network</Label>
              <Combobox
                placeholder="Select payment network"
                options={PaymentNetworkOptions}
                value={selectedNetwork}
                onChange={(value) => {
                  if (value === PAYMENT_NETWORK_ACH || value === PAYMENT_NETWORK_WIRE) {
                    setSelectedNetwork(value)
                    setSelectedBankAccountId('')
                  }
                }}
              />
            </div>
          </div>
        )}

        {((useUnverifiedAccount && selectedNetwork) || (selectedSupplierId && selectedNetwork)) && (
          <div className="space-y-4">
            <div className="font-semibold text-lg">Step 3: Select Bank Account</div>
            <div className="space-y-2">
              {useUnverifiedAccount ? (
                !isLoadingTraderBankAccounts ? (
                  <>
                    {unverifiedTraderAccounts.filter((account) =>
                      account.supportedPaymentNetworks.includes(selectedNetwork as BankPaymentNetwork)
                    ).length > 0 ? (
                      <>
                        <Label>Unverified Bank Account</Label>
                        <Combobox
                          placeholder="Select an unverified bank account"
                          options={unverifiedTraderAccounts
                            .filter((account) => account.id != null)
                            .filter((account) =>
                              account.supportedPaymentNetworks.includes(selectedNetwork as BankPaymentNetwork)
                            )
                            .map((account) => ({
                              label: `${account.bankName} ••••${account.last4} - ${account.accountHolderName}`,
                              value: account.id!.toString()
                            }))}
                          value={selectedBankAccountId}
                          onChange={setSelectedBankAccountId}
                          error={addressError}
                        />
                        {addressError?.message && <p className="text-sm text-red-500">{addressError.message}</p>}
                      </>
                    ) : (
                      <div className="flex flex-col gap-2 items-center">
                        <p>No unverified bank accounts found for the selected payment network.</p>
                      </div>
                    )}
                  </>
                ) : (
                  <Combobox
                    disabled
                    placeholder="Loading unverified accounts..."
                    options={[
                      {
                        label: 'loading...',
                        value: ''
                      }
                    ]}
                    value={selectedBankAccountId}
                    onChange={(value) => {
                      setSelectedBankAccountId(value)
                    }}
                  />
                )
              ) : !isLoadingSupplierBankAccounts ? (
                <>
                  {supportedPaymentNetworkBankAccounts.length > 0 ? (
                    <>
                      <Label>Bank Account</Label>
                      <Combobox
                        placeholder="Select a bank account"
                        options={supplierBankAccounts
                          .filter((account) => account.id != null)
                          .filter((account) =>
                            account.supportedPaymentNetworks.includes(selectedNetwork as BankPaymentNetwork)
                          )
                          .map((account) => ({
                            label: `${account.bankName} (${account.last4})`,
                            value: account.id!.toString()
                          }))}
                        value={selectedBankAccountId}
                        onChange={setSelectedBankAccountId}
                        error={addressError}
                      />
                      {addressError?.message && <p className="text-sm text-red-500">{addressError.message}</p>}
                      <div className="flex flex-col items-center gap-2 mt-2">
                        <p>OR</p>
                        <Button variant="outline" onClick={handleAddSupplierBankAccount}>
                          Add Supplier Bank Account
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col gap-2 items-center">
                      <p>No bank accounts found for this supplier and payment network.</p>
                      <Button variant="outline" onClick={handleAddSupplierBankAccount}>
                        Add Supplier Bank Account
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <Combobox
                  disabled
                  placeholder="Select a bank account"
                  options={[
                    {
                      label: 'loading...',
                      value: ''
                    }
                  ]}
                  value={selectedBankAccountId}
                  onChange={(value) => {
                    setSelectedBankAccountId(value)
                  }}
                />
              )}
            </div>
          </div>
        )}

        {selectedNetwork && selectedBankAccountId && (
          <div className="space-y-4">
            <div className="font-semibold text-lg">Step 4: Transfer Details</div>
            <div>
              <MoneyInput
                label="Amount"
                placeholder="0.00"
                value={amount}
                onChange={setAmount}
                helperText={balanceHelperText}
                max={availableBalance}
              />
            </div>
            <PaymentReferenceInput
              network={selectedNetwork as BankPaymentNetwork}
              value={statementDescriptor}
              onChange={setStatementDescriptor}
            />
          </div>
        )}

        {errors.length > 0 && (
          <Alert variant="destructive" className="mb-4 flex-shrink-0 max-h-[20%] overflow-y-auto">
            <AlertTitle>There are errors in your submission</AlertTitle>
            <AlertDescription>
              <ul className="list-disc pl-5">
                {errors.map((msg, idx) => (
                  <li key={idx}>{msg}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
        <div className="flex justify-end mt-5">
          <Button type="submit" disabled={isCreating || !selectedBankAccountId || errors.length > 0}>
            {isCreating ? 'Processing...' : 'Make Payment'}
          </Button>
        </div>
      </form>
    </section>
  )
}
